import { type JSX } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';

import usePaymentOverdue from 'app/hooks/usePaymentOverdue';
import { PrimaryButton } from 'app/shared-components/buttons';

import config from '@/config.json';

interface PaymentOverdueGateProps {
  container?: Element | (() => Element | null) | null;
}

export default function PaymentOverdueGate({
  container,
}: PaymentOverdueGateProps): JSX.Element | null {
  const { isBlocked, primaryUserEmail } = usePaymentOverdue();

  if (!isBlocked) return null;

  const openPaymentPortal = (): void => {
    window.open(
      `https://${config.chargebeeSite}.chargebeeportal.com`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Modal
      open
      hideBackdrop
      disableEscapeKeyDown
      container={container}
      className={`${
        container ? 'absolute' : 'fixed'
      } z-[9999] flex items-center justify-center bg-black/60 vtwo:p-4`}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-overdue-title"
        tabIndex={-1}
        className="w-full max-w-[440px] rounded-lg bg-white shadow-lg outline-none"
      >
        <Box className="flex items-center vtwo:gap-2 vtwo:px-6 vtwo:py-4 text-typography-danger">
          <FeedbackIcon fontSize="small" />
          <Typography id="payment-overdue-title" variant="subtitle1" className="font-600">
            Urgent Notice
          </Typography>
        </Box>
        <Box className="border-t border-border" />
        <Box className="flex flex-col vtwo:gap-4 vtwo:px-6 vtwo:py-4">
          <Typography variant="body2" className="text-typography-primary">
            We were not able to process your payment. Please update your payment method to continue
            using Healthera Connect.
          </Typography>
          <Typography variant="body2" className="text-typography-secondary">
            <AdminGuidance email={primaryUserEmail} />
          </Typography>
          <PrimaryButton label="Update Payment Method" cb={openPaymentPortal} size="m" fullWidth />
        </Box>
      </Box>
    </Modal>
  );
}

function AdminGuidance({ email }: { email: string | undefined }): JSX.Element {
  if (!email) {
    return (
      <>
        If you are the account admin, please log in to update your payment method; otherwise, please
        contact your account admin.
      </>
    );
  }

  return (
    <>
      If you are the account admin, please use the admin email{' '}
      <span className="font-600 text-typography-primary">{email}</span> to login; otherwise, please
      contact your account admin.
    </>
  );
}
